import Service, { type Registry as Services, service } from '@ember/service';
// eslint-disable-next-line import-x/no-named-as-default
import DS from 'ember-data';
import { cached, tracked } from '@glimmer/tracking';
import { min } from 'date-fns/min';
import { max } from 'date-fns/max';
import { startOfDay } from 'date-fns/startOfDay';
import { getOwner } from '@ember/application';
import { eachWeekOfInterval } from 'date-fns/eachWeekOfInterval';
import groupBy from 'lodash-es/groupBy';
import type Model from '@ember-data/model';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { routerHelper } from 'uplisting-frontend/utils/router';
import BookingModel, { BookingStatus } from 'uplisting-frontend/models/booking';
import EnquiryModel from 'uplisting-frontend/models/enquiry';
import { TaskKind } from 'uplisting-frontend/models/task';
import { formatLocalDate } from 'uplisting-frontend/utils/date';
import { OccasionRecord } from 'uplisting-frontend/services/calendar/-internals/record';
import {
  occasionStartDate,
  occasionEndDate,
} from 'uplisting-frontend/services/calendar/-internals/meta';
import { ConflictRecord } from 'uplisting-frontend/services/calendar/-internals/conflict';
import { RowOption } from 'uplisting-frontend/pods/xcalendar/controller';
import MultiUnitModel from 'uplisting-frontend/models/multi-unit';
import type MonthlyCalendarController from 'uplisting-frontend/pods/monthly/controller';
import type {
  LocationType,
  LocationField,
  CacheForLookup,
  CacheLookupItem,
  PropertyRowDataItem,
  CacheForLookupInner,
} from 'uplisting-frontend/services/calendar/-internals/types';
import {
  getFirstLevelCacheKey,
  getSecondLevelCacheKey,
  occasionCacheDate,
  forEachOccasionRecord,
  computeOccasionMetaForCalendar,
  belongsToLocation,
} from 'uplisting-frontend/services/calendar/-internals/utils';
import { clashes } from 'uplisting-frontend/services/calendar/-internals/occupancy';

export default class CalendarData extends Service {
  @service store!: Services['store'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @tracked rateCacheForLookup: CacheForLookup<ModelRegistry['rate']> = {};
  @tracked changeoverCacheForLookup: CacheForLookup<
    ModelRegistry['changeover']
  > = {};
  @tracked propertyCacheForLookup: CacheForLookup<OccasionRecord[]> = {};
  @tracked multiUnitCacheForLookup: CacheForLookup<OccasionRecord[]> = {};
  @tracked taskCacheForLookup: CacheForLookup<ModelRegistry['task'][]> = {};
  @tracked multiUnitTaskCacheForLookup: CacheForLookup<
    ModelRegistry['task'][]
  > = {};

  @tracked propertyConflictCacheForLookup: CacheForLookup<ConflictRecord> = {};
  @tracked multiUnitConflictCacheForLookup: CacheForLookup<ConflictRecord> = {};

  @tracked conflicts: ConflictRecord[] = [];

  // Cache to store OccasionRecord instances by model ID to enable reuse
  #occasionRecordCache = new Map<string, OccasionRecord>();

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.store.peekAll('property').slice();
  }

  @cached
  get multiUnits(): ModelRegistry['multi-unit'][] {
    return this.store.peekAll('multi-unit').slice();
  }

  @cached
  get tasksByProperty(): Record<string, ModelRegistry['task'][]> {
    return groupBy(this.#getAllFor('task'), (task) => task.property?.id);
  }

  @cached
  get enquiriesByProperty(): Record<string, ModelRegistry['enquiry'][]> {
    return groupBy(
      this.#getAllFor('enquiry'),
      (enquiry) => enquiry.property?.id,
    );
  }

  get cachesEnquiries(): boolean {
    return this.#targetSection !== 'calendar';
  }

  @cached
  get monthlyLocation(): LocationType | null {
    const owner = getOwner(this);
    const controller = owner?.lookup(
      'controller:monthly',
    ) as MonthlyCalendarController;

    return controller?.location;
  }

  public resetState(): void {
    this.store.unloadAll('rate');
    this.store.unloadAll('changeover');
    this.store.unloadAll('calendar');
    this.store.unloadAll('unavailability');

    this.#occasionRecordCache.clear();
    this.conflicts = [];
  }

  public setupFasterLookup(): void {
    if (!this.calendarManager.viewport) {
      return;
    }

    const section = this.#targetSection;

    this.#clearCaches();

    this.#setupRateCaches();

    // faster lookup is only needed when user is on the calendar
    if (section === 'calendar' || section === 'xcalendar') {
      this.#setupFasterLookupForCalendar();
    } else if (section === 'monthly') {
      this.#setupTaskCaches();
      this.#setupFasterLookupForMonthly();
    }

    forEachOccasionRecord(this.propertyCacheForLookup, (records) => {
      computeOccasionMetaForCalendar(records);
      // TODO: consider fixing/finding a better algorithm for this?
      // that's the easiest way to find all occasions that overlap with each other
      // we have records 1,2,3,4,5. record 1 overlaps with 2. record 2 overlaps with 3,4,5.
      // conflict record should be created for all of them.
      // but record 1 overlaps only with record 2.
      // easiest way to fix it is to call that method twice, as inside of it for each record we will check if it overlaps with any other conflict.
      // so after first call - conflict is created for records 2,3,4,5.
      // then on second call we see that record 1 overlaps with the conflict and is added to it.
      this.#computeOccasionConflicts(records);
      this.#computeOccasionConflicts(records);
    });

    forEachOccasionRecord(this.multiUnitCacheForLookup, (records) => {
      computeOccasionMetaForCalendar(records);
      this.#computeOccasionConflicts(records);
      this.#computeOccasionConflicts(records);
    });

    this.#addHeldConflicts();
    this.#computeConflictsCache();
  }

  public setupRateLookup(): void {
    this.#setupRateCaches();
  }

  public recordFor(occasion: PropertyRowDataItem): OccasionRecord {
    return this.#getOrCreateOccasionRecord(occasion);
  }

  /**
   * get cached/optimized data for specific record
   */
  public getCacheFor(
    field: RowOption,
    date: Date,
    id: string,
  ): CacheLookupItem {
    const dateFormatted = getSecondLevelCacheKey(date);

    switch (field) {
      case RowOption.properties:
        return this.propertyCacheForLookup[id]?.[dateFormatted];
      case RowOption.multiUnits:
        return this.multiUnitCacheForLookup[id]?.[dateFormatted];
      case RowOption.changeovers:
        return this.changeoverCacheForLookup[id]?.[dateFormatted];
      default:
        return this.rateCacheForLookup[id]?.[dateFormatted];
    }
  }

  public getAllCacheFor(field: LocationField, id: string): OccasionRecord[] {
    if (field === RowOption.properties) {
      return Object.values(this.propertyCacheForLookup[id] || {}).flat();
    } else if (field === RowOption.multiUnits) {
      return Object.values(this.multiUnitCacheForLookup[id] || {}).flat();
    }

    return [];
  }

  public getTasksFor(
    location: LocationType,
    date: Date,
  ): ModelRegistry['task'][] {
    const cache =
      location instanceof MultiUnitModel
        ? this.multiUnitTaskCacheForLookup
        : this.taskCacheForLookup;

    return cache[location.id]?.[getSecondLevelCacheKey(date)] ?? [];
  }

  public getAllTasksFor(
    property: ModelRegistry['property'],
    date: Date,
  ): ModelRegistry['task'][] {
    const forUnits = (property.multiUnits ?? []).flatMap((unit) =>
      this.getTasksFor(unit, date),
    );

    return [...this.getTasksFor(property, date), ...forUnits].sort(
      (a, b) =>
        (a.dueAtAsDate as Date).getTime() - (b.dueAtAsDate as Date).getTime(),
    );
  }

  public getConflictRecordForDate(
    location: LocationType,
    date: Date,
    id: string,
  ): ConflictRecord | undefined {
    const dateFormatted = getSecondLevelCacheKey(date);

    const cache =
      location instanceof MultiUnitModel
        ? this.multiUnitConflictCacheForLookup
        : this.propertyConflictCacheForLookup;

    return cache[id]?.[dateFormatted];
  }

  public async reloadRatesFor(
    properties: ModelRegistry['property'][],
  ): Promise<void> {
    const { startDate, endDate } = this.calendarManager.viewport;

    const promises: DS.PromiseArray<ModelRegistry['rate']>[] = [];

    for (const property of properties) {
      promises.push(
        this.store.query('rate', {
          from: formatLocalDate(startDate),
          property_id: property.id,
          to: formatLocalDate(endDate),
        }),
      );
    }

    await Promise.all(promises);

    this.setupFasterLookup();
  }

  #setupFasterLookupForCalendar(): void {
    // is needed only for the old calendar
    this.#setupChangeoverCaches();
    this.#setupOccasionCachesForCalendar();

    for (const property of this.properties) {
      this.#computeMonthlyOccasionsOverlap(property);
    }

    for (const multiUnit of this.multiUnits) {
      this.#computeMonthlyOccasionsOverlap(multiUnit);
    }
  }

  #setupFasterLookupForMonthly(): void {
    this.#setupOccasionCachesForMonthly();
    this.#computeMonthlyOccasionsOverlap(this.monthlyLocation);
  }

  #addHeldConflicts(): void {
    const held = [...this.calendarManager.resolution.heldRecords].sort(
      (a, b) => a.meta.startDate.getTime() - b.meta.startDate.getTime(),
    );

    const runs: OccasionRecord[][] = [];

    for (const record of held) {
      const run = runs.find(
        (records) =>
          records[0]?.location === record.location &&
          records.some(
            (other) =>
              record.meta.startDate < other.meta.endDate &&
              record.meta.endDate > other.meta.startDate,
          ),
      );

      if (run) {
        run.push(record);
      } else {
        runs.push([record]);
      }
    }

    this.conflicts.push(
      ...runs.map((records) => new ConflictRecord(this, records, true)),
    );
  }

  #computeConflictsCache(): void {
    this.#computeConflictsCacheFor(
      this.properties,
      'propertyConflictCacheForLookup',
    );

    this.#computeConflictsCacheFor(
      this.multiUnits,
      'multiUnitConflictCacheForLookup',
    );
  }

  /**
   * for the monthly calendar we show weeks as vertical blocks on top of each other
   * so we need to place conflict record in cache for each week first date in the conflict range
   * for the normal calendar we show days as a single horizontal block, so we need to place conflict record in cache for each day in the conflict range
   *
   * for the monthly view that method will return all first days of weeks in the conflict range
   * for the normal view that method will return a start date of the viewport
   *
   * @param conflict - conflict record
   * @returns dates to place conflict record in cache for
   */
  #datesForConflict(conflict: ConflictRecord): Date[] {
    const { normalViewport, isOldCalendar } = this.calendarManager;
    const { weekStartsOn } = this.calendarManager.utils;

    const isNewCalendar = !!normalViewport && !isOldCalendar;

    if (isNewCalendar) {
      return [normalViewport.startDate];
    }

    return eachWeekOfInterval(
      {
        start: conflict.startDate,
        end: conflict.endDate,
      },
      { weekStartsOn },
    );
  }

  #computeConflictsCacheFor(
    locations: LocationType[],
    cacheField:
      | 'propertyConflictCacheForLookup'
      | 'multiUnitConflictCacheForLookup',
  ): void {
    const { conflicts } = this;

    if (!conflicts.length) {
      return;
    }

    const cache = {};

    for (const location of locations) {
      const cacheKey = getFirstLevelCacheKey(location);

      for (const conflict of conflicts) {
        if (conflict.location !== location) {
          continue;
        }

        const datesForConflict = this.#datesForConflict(conflict);

        if (!cache[cacheKey]) {
          cache[cacheKey] = {};
        }

        cache[cacheKey][getSecondLevelCacheKey(conflict.startDate)] = conflict;

        // place conflict record in cache for each week in the conflict range
        for (const date of datesForConflict) {
          if (date <= conflict.startDate) {
            continue;
          }

          const dateKey = getSecondLevelCacheKey(date);

          if (!cache[cacheKey]) {
            cache[cacheKey] = {};
          }

          if (cache[cacheKey][dateKey] === conflict) {
            continue;
          }

          cache[cacheKey][dateKey] = conflict;
        }
      }
    }

    this[cacheField] = cache;
  }

  #computeMonthlyOccasionsOverlap(location: LocationType | null): void {
    if (!location) {
      return;
    }

    const isMultiUnit = location instanceof MultiUnitModel;
    const field = isMultiUnit ? RowOption.multiUnits : RowOption.properties;

    const cache = this.getAllCacheFor(field, location.id);

    for (const record of cache) {
      const { startDate, endDate } = record.meta;

      for (const otherRecord of cache) {
        if (otherRecord === record) {
          continue;
        }

        if (
          startDate < otherRecord.meta.endDate &&
          endDate > otherRecord.meta.startDate
        ) {
          record.meta.hasOverlap = true;

          break;
        }
      }
    }
  }

  /**
   * for all occasions of location(property/multi unit) create a conflict record if there are 3 or more occasions that overlap
   * occasions(instances of OccasionRecord) if present in any conflict record - are not displayed in the monthly calendar
   */
  #computeOccasionConflicts(records: OccasionRecord[]): void {
    for (const record of records) {
      const { location } = record;
      const { startDate, endDate } = record.meta;

      const existingConflict = this.#getExistingConflict(
        startDate,
        endDate,
        location,
      );

      if (existingConflict && !existingConflict.records.includes(record)) {
        existingConflict.records.push(record);

        continue;
      }

      const overlappingRecords = records.filter((otherRecord) => {
        if (otherRecord === record) {
          return false;
        }

        return (
          startDate < otherRecord.meta.endDate &&
          endDate > otherRecord.meta.startDate
        );
      });

      if (
        overlappingRecords.some((other) =>
          clashes(location, record.model, other.model),
        )
      ) {
        this.#createConflictFor([record, ...overlappingRecords], location);

        continue;
      }

      if (overlappingRecords.length <= 1) {
        continue;
      }

      const shouldCreateConflict = overlappingRecords.some(
        (record) => record.meta.rowNumber >= 2,
      );

      if (shouldCreateConflict) {
        this.#createConflictFor([record, ...overlappingRecords], location);
      }
    }
  }

  #getExistingConflict(
    start: Date,
    end: Date,
    location: LocationType,
  ): ConflictRecord | undefined {
    return this.conflicts.find(
      (conflict) =>
        conflict.startDate < end &&
        conflict.endDate > start &&
        conflict.location === location,
    );
  }

  /**
   * creates a conflict record for a given list of occasions(used when occasions overlap)
   * in case if records overlap with existing conflict, they are merged into the existing conflict
   *
   * @param records - occasions that are in conflict
   */
  #createConflictFor(records: OccasionRecord[], location: LocationType): void {
    const unique = [...new Set(records)];

    if (!unique.length) {
      return;
    }

    const rangeStart = min(unique.map((r) => r.meta.startDate));
    const rangeEnd = max(unique.map((r) => r.meta.endDate));

    const existingConflict = this.#getExistingConflict(
      rangeStart,
      rangeEnd,
      location,
    );

    if (existingConflict) {
      const merged = [...existingConflict.records];

      for (const record of unique) {
        if (!merged.includes(record)) {
          merged.push(record);
        }
      }

      existingConflict.records = merged;
    } else {
      this.conflicts.push(new ConflictRecord(this, unique));
    }
  }

  #clearCaches(): void {
    this.rateCacheForLookup = {};
    this.changeoverCacheForLookup = {};
    this.propertyCacheForLookup = {};
    this.multiUnitCacheForLookup = {};
    this.taskCacheForLookup = {};
    this.multiUnitTaskCacheForLookup = {};

    this.conflicts = [];

    this.propertyConflictCacheForLookup = {};
    this.multiUnitConflictCacheForLookup = {};

    this.#occasionRecordCache.clear();
  }

  /**
   * Get existing OccasionRecord or create a new one if it doesn't exist
   */
  #getOrCreateOccasionRecord(occasion: PropertyRowDataItem): OccasionRecord {
    const cacheKey = `${(occasion.constructor as typeof Model).modelName as string}-${occasion.id}`;

    let record = this.#occasionRecordCache.get(cacheKey);

    if (!record) {
      record = new OccasionRecord(this, occasion);

      this.#occasionRecordCache.set(cacheKey, record);
    }

    return record;
  }

  // Update cache for a specific occasion
  #updateOccasionCache(
    location: LocationType,
    date: Date,
    record: OccasionRecord,
  ): void {
    const cacheKey = getFirstLevelCacheKey(location);
    const dateKey = getSecondLevelCacheKey(date);

    const cache =
      location instanceof MultiUnitModel
        ? this.multiUnitCacheForLookup
        : this.propertyCacheForLookup;

    if (!cache[cacheKey]) {
      cache[cacheKey] = {};
    }

    if (!cache[cacheKey][dateKey]) {
      cache[cacheKey][dateKey] = [];
    }

    if (cache[cacheKey][dateKey].includes(record)) {
      return;
    }

    cache[cacheKey][dateKey].push(record);
  }

  // setup occasion caches for bookings, enquiries, booking requests, and unavailabilities
  #setupOccasionCachesForCalendar(): void {
    const bookings = this.#getAllFor('booking');
    const enquiries = this.cachesEnquiries ? this.#getAllFor('enquiry') : [];
    const bookingRequests = this.#getAllFor('booking-request');
    const unavailabilities = this.#getAllFor('unavailability');

    this.#setupCacheForOccasions(bookings);
    this.#setupCacheForOccasions(enquiries);
    this.#setupCacheForOccasions(bookingRequests);
    this.#setupCacheForOccasions(unavailabilities);
  }

  #setupOccasionCachesForMonthly(): void {
    const bookings = this.#getAllFor('booking');
    const enquiries = this.#getAllFor('enquiry');
    const bookingRequests = this.#getAllFor('booking-request');
    const unavailabilities = this.#getAllFor('unavailability');

    this.#setupCacheForOccasionsMonthly(bookings);
    this.#setupCacheForOccasionsMonthly(enquiries);
    this.#setupCacheForOccasionsMonthly(bookingRequests);
    this.#setupCacheForOccasionsMonthly(unavailabilities);
  }

  // setup cache for a specific type of occasions
  #setupCacheForOccasions(occasions: PropertyRowDataItem[]): void {
    const { dates } = this.calendarManager.viewport;

    for (const occasion of occasions) {
      // cancelled bookings can be retrieved when directly visiting URL
      // we want to allow them being accessible in the RHS, but not being displayed in the calendar
      if (
        occasion instanceof BookingModel &&
        occasion.status === BookingStatus.cancelled
      ) {
        continue;
      }

      // dateless enquiries can be loaded into the store when directly visiting
      // their URL; they have no dates to be positioned by on the calendar
      if (occasion instanceof EnquiryModel && occasion.isDateless) {
        continue;
      }

      if (this.calendarManager.resolution.isHeld(occasion)) {
        continue;
      }

      const date = occasionCacheDate(
        occasionStartDate(occasion),
        occasionEndDate(occasion),
        dates,
      );

      if (!date) {
        continue;
      }

      const { property } = occasion;
      const multiUnit = 'multiUnit' in occasion ? occasion.multiUnit : null;

      // Reuse existing OccasionRecord or create a new one
      const record = this.#getOrCreateOccasionRecord(occasion);

      if (multiUnit) {
        this.#updateOccasionCache(multiUnit, date, record);
      } else if (property) {
        this.#updateOccasionCache(property, date, record);
      }
    }
  }

  #setupCacheForOccasionsMonthly(occasions: PropertyRowDataItem[]): void {
    const { weekStartsOn } = this.calendarManager.utils;

    const location = this.monthlyLocation;

    if (!location) {
      return;
    }

    for (const occasion of occasions) {
      if (!belongsToLocation(location, occasion)) {
        continue;
      }

      if (occasion instanceof EnquiryModel && occasion.isDateless) {
        continue;
      }

      if (this.calendarManager.resolution.isHeld(occasion)) {
        continue;
      }

      const record = this.#getOrCreateOccasionRecord(occasion);

      this.#updateOccasionCache(location, record.meta.startDate, record);

      const weeks = eachWeekOfInterval(
        {
          start: record.meta.startDate,
          end: record.meta.endDate,
        },
        { weekStartsOn },
      );

      for (const week of weeks) {
        if (week > record.meta.startDate) {
          this.#updateOccasionCache(location, week, record);
        }
      }
    }
  }

  // the transition's target, as the model hook builds the caches before its route is current
  get #targetSection(): string | undefined {
    return routerHelper.targetRoute?.split('.')[0];
  }

  #getAllFor<K extends keyof ModelRegistry>(modelName: K): ModelRegistry[K][] {
    return this.store.peekAll(modelName).filter((record) => record.id);
  }

  #setupRateCaches(): void {
    this.#setupFasterLookupFor('rate', 'date', 'rateCacheForLookup');
  }

  #setupTaskCaches(): void {
    const tasks = this.store.peekAll('task').slice();

    const propertyCache: CacheForLookup<ModelRegistry['task'][]> = {};
    const multiUnitCache: CacheForLookup<ModelRegistry['task'][]> = {};

    for (const task of tasks) {
      const { property, multiUnit, dueAtAsDate } = task;

      if (
        !property ||
        !dueAtAsDate ||
        task.isCancelled ||
        task.kind !== TaskKind.cleaning
      ) {
        continue;
      }

      const cache = multiUnit ? multiUnitCache : propertyCache;
      const locationId = multiUnit ? multiUnit.id : property.id;

      const dateKey = getSecondLevelCacheKey(startOfDay(dueAtAsDate));

      const forLocation = (cache[locationId] ??= {});
      const forDate = (forLocation[dateKey] ??= []);

      forDate.push(task);
    }

    for (const cache of [propertyCache, multiUnitCache]) {
      for (const forLocation of Object.values(cache)) {
        for (const tasksForDate of Object.values(forLocation)) {
          tasksForDate.sort(
            (a, b) =>
              (a.dueAtAsDate as Date).getTime() -
              (b.dueAtAsDate as Date).getTime(),
          );
        }
      }
    }

    this.taskCacheForLookup = propertyCache;
    this.multiUnitTaskCacheForLookup = multiUnitCache;
  }

  #setupChangeoverCaches(): void {
    this.#setupFasterLookupFor(
      'changeover',
      'bookingEndDate',
      'changeoverCacheForLookup',
    );
  }

  #setupFasterLookupFor(
    modelName: string,
    dateField: string,
    cacheField: 'rateCacheForLookup' | 'changeoverCacheForLookup',
  ): void {
    const records = this.store.peekAll(modelName).slice();

    const cache = {};

    for (const record of records) {
      const { property } = record;

      if (!property) {
        continue;
      }

      const { id } = property;

      const recordDateFormatted = getSecondLevelCacheKey(record[dateField]);

      if (!cache[id]) {
        cache[id] = {};
      }

      (
        cache[id] as CacheForLookupInner<
          ModelRegistry['rate'] | ModelRegistry['changeover']
        >
      )[recordDateFormatted] = record;
    }

    this[cacheField] = cache;
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/data': CalendarData;
  }
}
